import {HttpClient} from '@angular/common/http';
import {computed, Injectable, Signal, signal, WritableSignal} from '@angular/core';
import {Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {CurrentUser, User} from '../models/user.model';
import {MessageHandlerService} from './messageHandler.service';
import {UserToken} from './authentication.service';
import {EnvironmentService} from './environment.service';
import {faro} from '@grafana/faro-web-sdk';

@Injectable({
  providedIn: 'root',
})
export class CurrentUserService {

  private readonly _currentUser$: WritableSignal<CurrentUser | undefined> = signal(undefined);

  readonly isMasterUser = computed(() => this._currentUser$()?.masterUser ?? false);

  private readonly serviceUrl: string = environment.baseUrl + '/users';

  constructor(
    private readonly http: HttpClient,
    private readonly messageService: MessageHandlerService,
    private readonly environmentService: EnvironmentService
  ) {
  }

  public getMe(userToken: UserToken): Observable<CurrentUser> {
    return this.http.get<CurrentUser>(`${this.serviceUrl}/me`).pipe(
      map((user: CurrentUser) => {
        const currentUser: CurrentUser = {
          ...user,
          token: userToken.token,
          ...userToken.data
        };

        this._currentUser$.set(currentUser);

        faro.api.setUser({
          username: currentUser.userName
        });

        this.environmentService.initCurrentEnvironment(currentUser);
        return currentUser;
      }),
      catchError(() => {
        this.messageService.handleError<User>(`Get me`);
        return of();
      })
    );
  }

  updateCurrentUser(updatedUser: Partial<CurrentUser>) {
    this._currentUser$.update((user) => {
      return {
        ...user!,
        ...updatedUser
      };
    });
  }

  public get currentUser$(): Signal<CurrentUser | undefined> {
    return this._currentUser$.asReadonly();
  }
}
